import { html, TemplateResult } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import type { HeadingLevel } from '../article-cards.interface';

const FALLBACK_IMAGE_SRC = '/content/dam/mhecomm/MHE-logo.svg';
const FALLBACK_IMAGE_ALT = 'McGraw Hill logo';
const FALLBACK_IMAGE_STYLE =
  'width: 6rem !important; height: 6rem !important; top: initial; left: 50%; margin-left: -3rem;';

/**
 * Builds the image element used inside article cards.
 * When imageUrl is falsy the MHE logo fallback is shown with a fixed inline style
 * that centres it inside the ratio-16x9 container — matches original articlecards.js behaviour exactly.
 */
export function buildImageElement(
  imageUrl: string,
  imageAlt: string,
  imageSize: string,
  imagePosition: string,
): TemplateResult {
  const src = imageUrl || FALLBACK_IMAGE_SRC;
  const alt = imageUrl ? (imageAlt || '') : FALLBACK_IMAGE_ALT;
  const style = imageUrl ? undefined : FALLBACK_IMAGE_STYLE;
  const sizeClass = imageSize ? `object-fit-${imageSize}` : '';
  const posClass = imagePosition ? `object-position-${imagePosition}` : '';
  // Match original: `w-100 h-100 ${sizeClass} ${posClass}` (trailing space harmless in CSS)
  const classes = `w-100 h-100 ${sizeClass} ${posClass}`;

  return html`<img
    alt="${alt}"
    class="${classes}"
    src="${src}"
    style="${ifDefined(style)}"
  />`;
}

/**
 * Builds a heading element at the specified level.
 * Used by featured and cards/manual templates only.
 * textHoriz and textVert hardcode <h4> and do NOT use this helper.
 *
 * Defaults to <h2> for any unrecognised level value — matches original switch default.
 */
export function buildHeading(
  level: HeadingLevel | string,
  className: string,
  text: string,
  id?: string,
): TemplateResult {
  const idAttr = ifDefined(id || undefined);
  switch (level) {
    case '3': return html`<h3 class=${className} id=${idAttr}>${text}</h3>`;
    case '4': return html`<h4 class=${className} id=${idAttr}>${text}</h4>`;
    case '5': return html`<h5 class=${className} id=${idAttr}>${text}</h5>`;
    case '6': return html`<h6 class=${className} id=${idAttr}>${text}</h6>`;
    default:  return html`<h2 class=${className} id=${idAttr}>${text}</h2>`;
  }
}
